import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { StaffRail } from '@/components/layout/StaffRail';
import { useRealtime, usePolling, fmtMoney, fmtTimeAgo } from '@/lib/realtime';
import { formatMoney, notifyPolicyUpdated } from '@/lib/policy';
import { checkPinStrength } from '@/lib/pin';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Plus, ShieldCheck, ShieldAlert, ShieldX, LogOut, Trash2, RefreshCw, MessageSquare, Upload, Pencil, FolderPlus, X, Users, Search, Phone, Copy, UserX, Save, AlertTriangle, Receipt, Lock, CheckCircle2, Truck, Award, Cake, Clock } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { OrderTypeBadge } from '@/components/layout/StaffRail';
import { API, getAccessToken } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS, getContextualStatusLabel } from '@/lib/orderStatus';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from 'recharts';

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [best, setBest] = useState([]);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  // Only owners can switch between branches in the picker. Other managers
  // (admins) and floor staff are pinned to their own assigned branch.
  const canSwitchBranch = user?.role === 'owner';
  const [smsLogs, setSmsLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const loadAll = useCallback(async () => {
    try {
      const [s, sb, bs, ords, mi, c, br, sl] = await Promise.all([
        api.get('/admin/stats/overview', { params: { branch_id: branchId || undefined } }),
        api.get('/admin/stats/sales-by-day', { params: { days: 7, branch_id: branchId || undefined } }),
        api.get('/admin/stats/best-sellers', { params: { branch_id: branchId || undefined } }),
        api.get('/orders', { params: { branch_id: branchId || undefined, limit: 100 } }),
        api.get('/menu/items', { params: { include_unavailable: true } }),
        api.get('/menu/categories'),
        api.get('/branches'),
        ['admin', 'owner'].includes(user.role) ? api.get('/admin/sms-logs', { params: { limit: 30 } }) : Promise.resolve({ data: [] }),
      ]);
      setStats(s.data);
      setSeries(sb.data?.series || []);
      setBest(bs.data?.items || []);
      setOrders(ords.data || []);
      setItems(mi.data || []);
      setCategories(c.data || []);
      setBranches(br.data || []);
      setSmsLogs(sl.data || []);
      // Force non-owners to their assigned branch on first load (and
      // overwrite any stale selection from a previous session).
      if (!canSwitchBranch && user?.branch_id && branchId !== user.branch_id) {
        setBranchId(user.branch_id);
      } else if (!branchId && (user?.branch_id || br.data?.[0])) {
        setBranchId(user?.branch_id || br.data[0].id);
      }
    } catch (e) {
      toast.error('Failed to load admin data');
    }
  }, [branchId, user?.role]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useRealtime(
    branchId ? [`admin:${branchId}`] : [],
    useCallback(() => { loadAll(); }, [loadAll]),
  );

  // Safety-net polling
  usePolling(loadAll, 10000, [branchId]);

  const chartData = useMemo(() => series.map((d) => ({ ...d, sales: +Number(d.sales).toFixed(2) })), [series]);

  return (
    <div className="min-h-screen bg-background">
      <StaffRail />
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3 sm:px-6">
        <div>
          <h1 className="font-display text-lg font-semibold">Admin Dashboard</h1>
          <p className="text-xs text-muted-foreground">{user?.full_name} · {user?.role}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canSwitchBranch ? (
            <select data-testid="admin-branch-select" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              {branches.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          ) : (
            <span data-testid="admin-branch-lock" className="inline-flex h-9 items-center gap-1 rounded-md border border-border bg-muted/40 px-3 text-xs text-muted-foreground" title="Only owners can switch branches.">
              {branches.find((b) => b.id === branchId)?.name || '\u2014'}
            </span>
          )}
          <Button data-testid="admin-refresh-button" size="sm" variant="secondary" onClick={loadAll}><RefreshCw className="mr-1 h-4 w-4" /> Refresh</Button>
          <Button data-testid="admin-go-kds-button" size="sm" variant="secondary" onClick={() => nav('/kds')}>KDS</Button>
          <Button data-testid="admin-go-pos-button" size="sm" variant="secondary" onClick={() => nav('/pos')}>POS</Button>
          <Button data-testid="admin-logout-button" size="sm" variant="ghost" onClick={() => { logout(); }}><LogOut className="mr-1 h-4 w-4" /> Sign out</Button>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList data-testid="admin-tabs" className="mb-4 flex w-full max-w-2xl flex-wrap gap-1">
            <TabsTrigger data-testid="admin-tab-overview" value="overview">Overview</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-orders" value="orders">Orders</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-customers" value="customers">Customers</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-policies" value="policies">Policies &amp; Settings</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-menu" value="menu">Menu</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-sms" value="sms">SMS log</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Kpi data-testid="admin-kpi-sales" label="Today's sales" value={fmtMoney(stats?.total_sales || 0)} />
              <Kpi data-testid="admin-kpi-paid" label="Paid sales" value={fmtMoney(stats?.paid_sales || 0)} />
              <Kpi data-testid="admin-kpi-orders" label="Orders today" value={stats?.order_count || 0} />
              <Kpi data-testid="admin-kpi-active" label="Active in kitchen" value={stats?.active_orders || 0} />
            </div>
            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader><CardTitle>Sales (last 7 days)</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="sales" stroke="hsl(var(--primary))" fill="url(#g1)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Best sellers</CardTitle></CardHeader>
                <CardContent className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={best.slice(0, 7)}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                      <XAxis dataKey="name" hide />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="quantity" fill="hsl(var(--accent))" radius={6} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between"><CardTitle>Recent orders</CardTitle><CardDescription className="text-xs">Click a row for details</CardDescription></CardHeader>
              <CardContent className="overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <tr><th className="p-2">Order</th><th className="p-2">Type</th><th className="p-2">Customer</th><th className="p-2">Status</th><th className="p-2">Payment</th><th className="p-2 text-right">Total</th><th className="p-2">When</th></tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <OrderRow key={o.id} order={o} onChange={loadAll} />
                    ))}
                  </tbody>
                </table>
                {!orders.length && <p className="p-6 text-center text-sm text-muted-foreground">No orders yet</p>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="customers">
            <CustomersDirectory />
          </TabsContent>

          <TabsContent value="policies">
            <PoliciesAndBansPanel />
          </TabsContent>

          <TabsContent value="menu">
            <MenuManagement items={items} categories={categories} onReload={loadAll} />
          </TabsContent>

          <TabsContent value="sms">
            <Card>
              <CardHeader><CardTitle>SMS log</CardTitle><CardDescription className="text-xs">Twilio is in placeholder mode — messages are logged. Add real Twilio credentials in <code className="bg-secondary px-1">backend/.env</code> to send real SMS.</CardDescription></CardHeader>
              <CardContent>
                <ul className="divide-y divide-border">
                  {smsLogs.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 py-3">
                      <MessageSquare className="mt-0.5 h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-sm">{s.message}</p>
                        <p className="mt-1 text-xs text-muted-foreground">to {s.to} · {s.kind} · {fmtTimeAgo(s.created_at)} · <span className={s.sent_via_twilio ? 'text-emerald-700' : 'text-amber-700'}>{s.status}</span></p>
                      </div>
                    </li>
                  ))}
                </ul>
                {!smsLogs.length && <p className="p-6 text-center text-sm text-muted-foreground">No SMS sent yet</p>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Kpi({ label, value, ...rest }) {
  return (
    <Card {...rest}>
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="mt-2 font-display text-3xl font-semibold tabular">{value}</p>
      </CardContent>
    </Card>
  );
}

function OrderRow({ order, onChange }) {
  const [open, setOpen] = useState(false);
  const markPaid = async () => {
    try { await api.post(`/payments/cash/mark-paid/${order.id}`); toast.success('Marked paid'); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const advance = async (status) => {
    try { await api.patch(`/orders/${order.id}/status`, { status }); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <>
      <tr data-testid="admin-order-row" className="cursor-pointer border-t border-border hover:bg-secondary/50" onClick={() => setOpen(true)}>
        <td className="p-2 font-semibold">#{order.order_number}</td>
        <td className="p-2"><OrderTypeBadge orderType={order.order_type} tableNumber={order.table_number} /></td>
        <td className="p-2">{order.customer_name}</td>
        <td className="p-2"><Badge className={`border ${ORDER_STATUS_COLORS[order.status] || ''}`}>{getContextualStatusLabel(order.status, order.order_type, order.table_number)}</Badge></td>
        <td className="p-2 text-xs capitalize">{order.payment_method} · {order.payment_status}</td>
        <td className="p-2 text-right tabular font-semibold">{fmtMoney(order.total)}</td>
        <td className="p-2 text-xs text-muted-foreground">{fmtTimeAgo(order.created_at)}</td>
      </tr>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>Order #{order.order_number}</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-3 text-sm">
            <p><span className="text-muted-foreground">Customer:</span> {order.customer_name} {order.customer_phone ? `· ${order.customer_phone}` : ''}</p>
            <p><span className="text-muted-foreground">Type:</span> {order.order_type} {order.table_number ? `· Table ${order.table_number}` : ''}</p>
            <p><span className="text-muted-foreground">Payment:</span> {order.payment_method} · {order.payment_status}</p>
            {order.delivery_verification_code && <p><span className="text-muted-foreground">Driver code:</span> <span className="rounded bg-secondary px-1 font-mono">{order.delivery_verification_code}</span></p>}
            <div className="rounded border border-border p-2">
              <ul className="space-y-1">
                {order.items?.map((it, idx) => (
                  <li key={idx} className="flex justify-between">
                    <span>{it.quantity}× {it.name}{it.special_instructions ? ` – ${it.special_instructions}` : ''}</span>
                    <span className="tabular">{fmtMoney(it.line_total)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex justify-between border-t border-border pt-2 font-semibold"><span>Total</span><span className="tabular">{fmtMoney(order.total)}</span></div>
            </div>
            <div className="flex flex-wrap gap-2">
              {order.payment_status !== 'paid' && order.payment_method === 'cash' && (
                <Button data-testid="admin-mark-paid-button" size="sm" onClick={markPaid}><ShieldCheck className="mr-1 h-4 w-4" /> Mark paid</Button>
              )}
              {order.payment_method !== 'cash' && order.payment_method !== 'card' && order.payment_status !== 'paid' && (
                <Button size="sm" onClick={async () => { try { await api.post(`/payments/mobile-money/confirm/${order.id}`); onChange(); toast.success('Mobile money confirmed'); } catch (e) { toast.error('Failed'); } }}>Confirm mobile money</Button>
              )}
              {order.status === 'received' && <Button size="sm" variant="secondary" onClick={() => advance('preparing')}>Start preparing</Button>}
              {order.status === 'preparing' && <Button size="sm" variant="secondary" onClick={() => advance('ready')}>Mark ready</Button>}
              {order.status === 'ready' && order.order_type === 'delivery' && <Button size="sm" variant="secondary" onClick={() => advance('out_for_delivery')}>Dispatch</Button>}
              {order.status === 'ready' && order.order_type !== 'delivery' && <Button size="sm" variant="secondary" onClick={() => advance('completed')}>Complete</Button>}
              {order.status === 'out_for_delivery' && <Button size="sm" variant="secondary" onClick={() => advance('delivered')}>Delivered</Button>}
              {!['cancelled', 'completed', 'delivered'].includes(order.status) && (
                <Button data-testid="admin-cancel-order-button" size="sm" variant="ghost" onClick={async () => { try { await api.post(`/orders/${order.id}/cancel`); onChange(); toast.success('Order cancelled'); setOpen(false); } catch (e) { toast.error('Failed'); } }}>Cancel</Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// Reusable image upload widget: accepts URL paste OR file upload, returns a URL.
function ImageUploader({ value, onChange, kind = 'menu' }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = getAccessToken();
      const res = await fetch(`${API}/uploads?kind=${encodeURIComponent(kind)}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || `Upload failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          data-testid="image-upload-url-input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or upload →"
          className="flex-1"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid="image-upload-file-input"
        />
        <Button
          data-testid="image-upload-trigger"
          type="button"
          size="sm"
          variant="secondary"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="mr-1 h-4 w-4" /> {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
      {value && (
        <div className="relative inline-block overflow-hidden rounded-md border border-border">
          <img src={value.startsWith('/api') ? `${API.replace(/\/api$/, '')}${value}` : value} alt="preview" className="h-24 w-40 object-cover" />
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="absolute right-1 top-1 h-6 w-6 rounded-full bg-black/40 text-white hover:bg-black/60"
            onClick={() => onChange('')}
            data-testid="image-upload-clear"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">JPEG/PNG/WebP/GIF up to 8 MB.</p>
    </div>
  );
}

const EMPTY_ITEM = { id: null, category_id: '', name: '', description: '', price: 0, image_url: '', tags: '', allergens: '', prep_time_minutes: 15, is_available: true };

function MenuManagement({ items, categories, onReload }) {
  const [editing, setEditing] = useState(null); // null when closed; { ...form }
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  // Category dialog
  const [catDialog, setCatDialog] = useState(null); // {mode:'add'|'edit', id, name}

  const openCreate = () => setEditing({ ...EMPTY_ITEM, category_id: filterCat !== 'all' ? filterCat : '' });
  const openEdit = (it) => setEditing({
    id: it.id,
    category_id: it.category_id || '',
    name: it.name || '',
    description: it.description || '',
    price: it.price || 0,
    image_url: it.image_url || '',
    tags: (it.tags || []).join(', '),
    allergens: (it.allergens || []).join(', '),
    prep_time_minutes: it.prep_time_minutes || 15,
    is_available: it.is_available !== false,
  });

  const submit = async () => {
    if (!editing.name || !editing.category_id || !editing.price) { toast.error('Name, category and price are required'); return; }
    const payload = {
      category_id: editing.category_id,
      name: editing.name,
      description: editing.description,
      price: Number(editing.price),
      image_url: editing.image_url,
      tags: editing.tags ? editing.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      allergens: editing.allergens ? editing.allergens.split(',').map((t) => t.trim()).filter(Boolean) : [],
      prep_time_minutes: Number(editing.prep_time_minutes),
      is_available: editing.is_available,
    };
    try {
      if (editing.id) {
        await api.patch(`/menu/items/${editing.id}`, payload);
        toast.success('Item updated');
      } else {
        await api.post('/menu/items', payload);
        toast.success('Item created');
      }
      setEditing(null);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggle = async (it) => {
    try {
      await api.patch(`/menu/items/${it.id}`, { is_available: !it.is_available });
      onReload();
    } catch (e) { toast.error('Failed'); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/menu/items/${it.id}`); onReload(); toast.success('Deleted'); } catch (e) { toast.error('Failed'); }
  };

  // Category operations
  const saveCategory = async () => {
    const { mode, id, name } = catDialog;
    if (!name || name.length < 2) { toast.error('Category name too short'); return; }
    try {
      if (mode === 'add') {
        await api.post('/menu/categories', { name });
        toast.success('Category added');
      } else {
        await api.patch(`/menu/categories/${id}`, { name });
        toast.success('Category renamed');
      }
      setCatDialog(null);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (e?.response?.status === 404
        ? 'Rename is not supported by the backend yet — please contact support.'
        : 'Failed'));
    }
  };
  const removeCategory = async (c) => {
    const usedCount = items.filter((i) => i.category_id === c.id).length;
    if (usedCount > 0) {
      toast.error(`Can't delete: ${usedCount} item${usedCount === 1 ? '' : 's'} still use "${c.name}"`);
      return;
    }
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/menu/categories/${c.id}`); toast.success('Category deleted'); onReload(); }
    catch (e) { toast.error('Failed'); }
  };

  // Filtered list
  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filterCat !== 'all' && it.category_id !== filterCat) return false;
      if (s && !(it.name || '').toLowerCase().includes(s) && !(it.description || '').toLowerCase().includes(s)) return false;
      return true;
    });
  }, [items, search, filterCat]);

  // Group categories with counts
  const catCounts = useMemo(() => {
    const m = {};
    for (const it of items) m[it.category_id] = (m[it.category_id] || 0) + 1;
    return m;
  }, [items]);

  return (
    <div className="space-y-4">
      {/* Categories management */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base">Categories</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">Rename categories like "Signature Grills" or "Signature Meals" anytime.</p>
          </div>
          <Button data-testid="menu-add-category-button" size="sm" variant="secondary" onClick={() => setCatDialog({ mode: 'add', name: '' })}>
            <FolderPlus className="mr-1 h-4 w-4" /> New category
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((c) => (
              <div key={c.id} data-testid="menu-category-chip" className="group inline-flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-3 py-1 text-xs">
                <span className="font-medium">{c.name}</span>
                <span className="text-muted-foreground">· {catCounts[c.id] || 0}</span>
                <button data-testid="menu-category-rename-button" className="ml-1 rounded p-0.5 opacity-60 hover:bg-primary/10 hover:text-primary hover:opacity-100" onClick={() => setCatDialog({ mode: 'edit', id: c.id, name: c.name })} aria-label="Rename">
                  <Pencil className="h-3 w-3" />
                </button>
                <button className="rounded p-0.5 opacity-60 hover:bg-rose-100 hover:text-rose-700 hover:opacity-100" onClick={() => removeCategory(c)} aria-label="Delete">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {!categories.length && <p className="text-xs text-muted-foreground">No categories yet.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Menu items</CardTitle>
            <Badge variant="secondary">{visible.length} / {items.length}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input data-testid="menu-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search items…" className="h-9 w-56" />
            <select
              data-testid="menu-filter-category"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="all">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <Button data-testid="admin-add-menu-button" size="sm" onClick={openCreate}>
              <Plus className="mr-1 h-4 w-4" /> Add item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!visible.length && (
            <p className="py-6 text-center text-sm text-muted-foreground">No items match your filter.</p>
          )}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((it) => {
              const cat = categories.find((c) => c.id === it.category_id);
              const imgSrc = (it.image_url || '').startsWith('/api')
                ? `${API.replace(/\/api$/, '')}${it.image_url}`
                : it.image_url;
              return (
                <Card key={it.id} data-testid="menu-admin-item-card" className={`overflow-hidden ${!it.is_available ? 'opacity-70' : ''}`}>
                  <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
                    {imgSrc
                      ? <img src={imgSrc} alt={it.name} className="h-full w-full object-cover" />
                      : <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">No image</div>}
                    {!it.is_available && (
                      <span className="absolute left-2 top-2 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">Disabled</span>
                    )}
                  </div>
                  <CardContent className="space-y-2 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className="truncate font-semibold leading-tight">{it.name}</h4>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{cat?.name || '—'}</p>
                      </div>
                      <span className="shrink-0 tabular text-sm font-semibold">{fmtMoney(it.price)}</span>
                    </div>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{it.description || '—'}</p>
                    {(it.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {(it.tags || []).slice(0, 3).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                      </div>
                    )}
                    <div className="flex items-center gap-1 pt-1">
                      <Button data-testid="admin-item-edit-button" size="sm" onClick={() => openEdit(it)}>
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => toggle(it)} data-testid="admin-item-toggle-button">
                        {it.is_available ? 'Disable' : 'Enable'}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(it)} className="ml-auto text-destructive" data-testid="admin-item-delete-button">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Edit / create dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit menu item' : 'New menu item'}</DialogTitle>
            <DialogDescription>{editing?.id ? 'Update name, image, price, ingredients tags and more.' : 'Add a new dish to your menu.'}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2 space-y-1.5">
                <Label>Image</Label>
                <ImageUploader value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} />
              </div>
              <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="admin-add-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Category *</Label>
                <select data-testid="admin-add-category-select" value={editing.category_id} onChange={(e) => setEditing({ ...editing, category_id: e.target.value })} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                  <option value="">Select category</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 space-y-1.5"><Label>Description</Label><Input data-testid="admin-add-desc-input" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Price (USD) *</Label><Input data-testid="admin-add-price-input" type="number" step="0.5" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Prep time (min)</Label><Input type="number" value={editing.prep_time_minutes} onChange={(e) => setEditing({ ...editing, prep_time_minutes: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Tags (comma separated)</Label><Input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} placeholder="signature, spicy, gf" /></div>
              <div className="space-y-1.5"><Label>Allergens (comma separated)</Label><Input value={editing.allergens} onChange={(e) => setEditing({ ...editing, allergens: e.target.value })} placeholder="gluten, dairy, nuts" /></div>
              <label className="md:col-span-2 mt-1 flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_available} onChange={(e) => setEditing({ ...editing, is_available: e.target.checked })} className="h-4 w-4 accent-primary" />
                Available for ordering
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="admin-add-submit-button" onClick={submit}>{editing?.id ? 'Save changes' : 'Create item'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category dialog */}
      <Dialog open={!!catDialog} onOpenChange={(o) => { if (!o) setCatDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{catDialog?.mode === 'add' ? 'New category' : 'Rename category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              data-testid="menu-category-name-input"
              value={catDialog?.name || ''}
              onChange={(e) => setCatDialog({ ...catDialog, name: e.target.value })}
              placeholder="e.g. Signature Meals"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCatDialog(null)}>Cancel</Button>
            <Button data-testid="menu-category-save-button" onClick={saveCategory}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}



const CHANNEL_TONE = {
  pickup: 'bg-purple-50 text-purple-900 border-purple-200',
  dine_in: 'bg-blue-50 text-blue-900 border-blue-200',
  delivery: 'bg-amber-50 text-amber-900 border-amber-200',
};
const CHANNEL_LABEL = { pickup: 'Pickup', dine_in: 'Dine-in', delivery: 'Delivery' };

function CustomersDirectory() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | banned | strikes
  const [confirmState, setConfirmState] = useState(null);
  const [strikeDetail, setStrikeDetail] = useState(null);
  const [strikeLoading, setStrikeLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/customers', { params: { limit: 500 } });
      setRows(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load customers');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'banned' && !r.banned) return false;
      if (filter === 'strikes' && !(r.strike_count > 0)) return false;
      if (!s) return true;
      return (r.name || '').toLowerCase().includes(s)
        || (r.phone || '').toLowerCase().includes(s);
    });
  }, [rows, search, filter]);

  const totalSpent = useMemo(
    () => filtered.reduce((acc, r) => acc + (r.total_spent || 0), 0),
    [filtered],
  );

  const bannedCount = useMemo(() => rows.filter((r) => r.banned).length, [rows]);
  const strikedCount = useMemo(() => rows.filter((r) => r.strike_count > 0 && !r.banned).length, [rows]);

  const copyPhone = (phone) => {
    try {
      navigator.clipboard.writeText(phone);
      toast.success(`Copied ${phone}`);
    } catch (e) { /* noop */ }
  };

  const requestBan = (row) => {
    setConfirmState({
      kind: 'ban',
      row,
      title: `Ban ${row.name || row.phone}?`,
      description: (
        `This will block ${row.phone} from placing any new pickup or delivery order across all channels (POS, customer web, iPad). `
        + 'A manager can lift the ban at any time from this directory.'
      ),
      confirmLabel: 'Yes, ban customer',
      destructive: true,
    });
  };

  const requestUnban = (row) => {
    setConfirmState({
      kind: 'unban',
      row,
      title: `Unban ${row.name || row.phone}?`,
      description: `Allow ${row.phone} to place orders again. Strike history is preserved \u2014 reaching the strike threshold again will re-trigger an auto-ban.`,
      confirmLabel: 'Yes, unban customer',
      destructive: false,
    });
  };

  const performAction = async () => {
    if (!confirmState) return;
    const { kind, row } = confirmState;
    setConfirmState(null);
    try {
      const phoneEnc = encodeURIComponent(row.phone);
      if (kind === 'ban') {
        await api.post(`/admin/customers/${phoneEnc}/ban`, { reason: 'Manual ban from Customers tab' });
        toast.success(`${row.name || row.phone} banned.`);
      } else if (kind === 'unban') {
        await api.delete(`/admin/customers/${phoneEnc}/ban`);
        toast.success(`${row.name || row.phone} unbanned.`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const viewStrikes = async (row) => {
    setStrikeDetail({ row, data: null });
    setStrikeLoading(true);
    try {
      const phoneEnc = encodeURIComponent(row.phone);
      const { data } = await api.get(`/admin/customers/${phoneEnc}/strikes`);
      setStrikeDetail({ row, data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load strikes');
      setStrikeDetail(null);
    } finally { setStrikeLoading(false); }
  };

  // ---- VIP + DOB inline editors ----
  const [dobEdit, setDobEdit] = useState(null); // { phone, name, value }
  const toggleVip = async (row) => {
    const next = !(row.vip_manual || row.vip);
    try {
      const phoneEnc = encodeURIComponent(row.phone);
      await api.patch(`/admin/customers/${phoneEnc}/profile`, { vip: next });
      toast.success(next ? `${row.name || row.phone} marked VIP` : `${row.name || row.phone} unmarked`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveDob = async () => {
    if (!dobEdit) return;
    try {
      const phoneEnc = encodeURIComponent(dobEdit.phone);
      await api.patch(`/admin/customers/${phoneEnc}/profile`, { date_of_birth: dobEdit.value || '' });
      toast.success('Date of birth updated');
      setDobEdit(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> Customer directory</CardTitle>
          <CardDescription className="text-xs">
            Aggregated from every pickup &amp; delivery order. Grouped by phone number, ordered by most recent activity. Strikes are counted from cancelled / no-show orders.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="admin-customers-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or phone…"
              className="pl-9"
            />
          </div>
          <Button size="icon" variant="ghost" onClick={load} title="Refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Button
            data-testid="admin-customers-filter-all"
            size="sm"
            variant={filter === 'all' ? 'default' : 'secondary'}
            onClick={() => setFilter('all')}
          >
            All <Badge variant="outline" className="ml-1.5 h-5 border-0 bg-background/40 px-1.5 text-[10px]">{rows.length}</Badge>
          </Button>
          <Button
            data-testid="admin-customers-filter-strikes"
            size="sm"
            variant={filter === 'strikes' ? 'default' : 'secondary'}
            onClick={() => setFilter('strikes')}
            className={filter === 'strikes' ? '' : 'text-amber-700'}
          >
            <AlertTriangle className="mr-1 h-3.5 w-3.5" /> With strikes
            <Badge variant="outline" className="ml-1.5 h-5 border-0 bg-background/40 px-1.5 text-[10px]">{strikedCount}</Badge>
          </Button>
          <Button
            data-testid="admin-customers-filter-banned"
            size="sm"
            variant={filter === 'banned' ? 'default' : 'secondary'}
            onClick={() => setFilter('banned')}
            className={filter === 'banned' ? '' : 'text-rose-700'}
          >
            <ShieldX className="mr-1 h-3.5 w-3.5" /> Banned
            <Badge variant="outline" className="ml-1.5 h-5 border-0 bg-background/40 px-1.5 text-[10px]">{bannedCount}</Badge>
          </Button>
          <span className="ml-auto flex flex-wrap gap-2 text-xs">
            <Badge variant="outline" data-testid="admin-customers-count">{filtered.length} customer{filtered.length === 1 ? '' : 's'}</Badge>
            <Badge variant="outline" data-testid="admin-customers-spent">Lifetime value: {fmtMoney(totalSpent)}</Badge>
          </span>
        </div>
        <div className="overflow-auto rounded-md border border-border">
          <table className="w-full text-sm" data-testid="admin-customers-table">
            <thead>
              <tr className="bg-secondary/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2 text-center">VIP</th>
                <th className="px-3 py-2">DOB</th>
                <th className="px-3 py-2 text-center">Strikes</th>
                <th className="px-3 py-2">Channels</th>
                <th className="px-3 py-2 text-right">Orders</th>
                <th className="px-3 py-2 text-right">Total spent</th>
                <th className="px-3 py-2">Last order</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">
                  {rows.length === 0 ? 'No customers yet \u2014 they\u2019ll appear here once you take pickup or delivery orders.' : 'No customers match that filter.'}
                </td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.phone} data-testid="admin-customer-row" className="border-t border-border hover:bg-secondary/40">
                  <td className="px-3 py-2">
                    {r.banned ? (
                      <Badge data-testid="admin-customer-status-banned" className="border border-rose-300 bg-rose-50 text-[10px] text-rose-800 hover:bg-rose-50">
                        <ShieldX className="mr-1 h-3 w-3" /> BANNED
                      </Badge>
                    ) : r.strike_count > 0 ? (
                      <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-900">
                        <AlertTriangle className="mr-1 h-3 w-3" /> At risk
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">
                        Active
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium">{r.name || '\u2014'}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid="admin-customer-phone"
                      onClick={() => copyPhone(r.phone)}
                      className="inline-flex items-center gap-1.5 font-mono text-sm hover:text-primary"
                      title="Click to copy"
                    >
                      <Phone className="h-3 w-3" />
                      {r.phone}
                      <Copy className="h-3 w-3 opacity-60" />
                    </button>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      data-testid="admin-customer-vip-toggle"
                      onClick={() => toggleVip(r)}
                      title={r.vip_manual ? 'Click to remove manual VIP flag' : r.vip ? 'Auto-VIP (10+ orders). Click to set manual VIP.' : 'Click to mark VIP'}
                      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold transition-colors ${r.vip_manual ? 'border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100' : r.vip ? 'border-sky-200 bg-sky-50 text-sky-900 hover:bg-sky-100' : 'border-border bg-background text-muted-foreground hover:bg-secondary'}`}
                    >
                      <Award className="h-3 w-3" /> {r.vip_manual ? 'VIP' : r.vip ? 'AUTO' : 'No'}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      data-testid="admin-customer-dob-edit"
                      onClick={() => setDobEdit({ phone: r.phone, name: r.name, value: r.date_of_birth || '' })}
                      className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-primary"
                      title="Edit date of birth"
                    >
                      <Cake className="h-3 w-3" /> {r.date_of_birth || 'Add'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {r.strike_count > 0 ? (
                      <button
                        type="button"
                        data-testid="admin-customer-view-strikes"
                        onClick={() => viewStrikes(r)}
                        className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-900 transition-colors hover:bg-amber-100"
                        title="View strike history"
                      >
                        {r.strike_count}
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.channels || []).map((ch) => (
                        <Badge key={ch} variant="outline" className={`border text-[10px] ${CHANNEL_TONE[ch] || ''}`}>
                          {CHANNEL_LABEL[ch] || ch}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular">{r.order_count}</td>
                  <td className="px-3 py-2 text-right tabular font-semibold">{fmtMoney(r.total_spent)}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtTimeAgo(r.last_order_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {r.banned ? (
                        <Button
                          data-testid="admin-customer-unban"
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => requestUnban(r)}
                        >
                          <ShieldCheck className="mr-1 h-3 w-3" /> Unban
                        </Button>
                      ) : (
                        <Button
                          data-testid="admin-customer-ban"
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                          onClick={() => requestBan(r)}
                        >
                          <UserX className="mr-1 h-3 w-3" /> Ban
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <AlertDialog open={!!confirmState} onOpenChange={(o) => { if (!o) setConfirmState(null); }}>
        <AlertDialogContent data-testid="admin-customer-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {confirmState?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-customer-confirm-cancel">Back</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-customer-confirm-accept"
              onClick={performAction}
              className={confirmState?.destructive ? 'bg-rose-600 text-white hover:bg-rose-700' : ''}
            >
              {confirmState?.confirmLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* === DOB editor === */}
      <Dialog open={!!dobEdit} onOpenChange={(o) => { if (!o) setDobEdit(null); }}>
        <DialogContent data-testid="admin-customer-dob-dialog">
          <DialogHeader>
            <DialogTitle>Date of birth — {dobEdit?.name || dobEdit?.phone}</DialogTitle>
            <DialogDescription>
              Used for birthday segments in Marketing. Leave blank to clear.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Birthday</Label>
            <Input
              data-testid="admin-customer-dob-input"
              type="date"
              value={dobEdit?.value || ''}
              onChange={(e) => setDobEdit({ ...dobEdit, value: e.target.value })}
            />
          </div>
          <DialogFooter className="gap-2">
            {dobEdit?.value && (
              <Button data-testid="admin-customer-dob-clear" variant="ghost" onClick={() => setDobEdit({ ...dobEdit, value: '' })}>Clear</Button>
            )}
            <Button data-testid="admin-customer-dob-save" onClick={saveDob}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={!!strikeDetail} onOpenChange={(o) => { if (!o) setStrikeDetail(null); }}>
        <SheetContent data-testid="admin-strikes-sheet" className="w-full max-w-md sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              Strike history{strikeDetail?.row ? ` \u2014 ${strikeDetail.row.name || strikeDetail.row.phone}` : ''}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3 text-sm">
            {strikeLoading && (
              <p className="text-muted-foreground">Loading…</p>
            )}
            {!strikeLoading && strikeDetail?.data && (
              <>
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900">
                    <AlertTriangle className="mr-1 h-3 w-3" /> {strikeDetail.data.strike_count} strike{strikeDetail.data.strike_count === 1 ? '' : 's'}
                  </Badge>
                  {strikeDetail.data.banned ? (
                    <Badge className="border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-50">
                      <ShieldX className="mr-1 h-3 w-3" /> Currently banned
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">Not banned</Badge>
                  )}
                </div>
                {(strikeDetail.data.strikes || []).length === 0 ? (
                  <p className="text-muted-foreground">No cancelled / no-show orders on record.</p>
                ) : (
                  <div className="overflow-auto rounded-md border border-border">
                    <table className="w-full text-xs" data-testid="admin-strikes-table">
                      <thead>
                        <tr className="bg-secondary/60 text-left uppercase tracking-wider text-muted-foreground">
                          <th className="px-2 py-1.5">Order #</th>
                          <th className="px-2 py-1.5">Type</th>
                          <th className="px-2 py-1.5">Reason</th>
                          <th className="px-2 py-1.5 text-right">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {strikeDetail.data.strikes.map((o) => (
                          <tr key={o.id} className="border-t border-border">
                            <td className="px-2 py-1.5 font-mono">#{o.order_number}</td>
                            <td className="px-2 py-1.5 capitalize">{(o.order_type || '').replace('_', ' ')}</td>
                            <td className="px-2 py-1.5">
                              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-900">
                                {(o.cancel_reason || '').replace(/_/g, ' ')}
                              </Badge>
                            </td>
                            <td className="px-2 py-1.5 text-right text-muted-foreground">{fmtTimeAgo(o.cancelled_at || o.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Policies & Bans tab
// ---------------------------------------------------------------------------
function PoliciesAndBansPanel() {
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [bans, setBans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmUnban, setConfirmUnban] = useState(null);

  // Backend stores tax as decimal (0.08) and tip suggestions as [10,15,20].
  // UI works in % and a CSV string. Convert at the edges.
  const decorate = (p) => {
    if (!p) return p;
    return {
      ...p,
      tax_rate_pct: Math.round((Number(p.tax_rate) || 0) * 10000) / 100,
      tip_suggestions_csv: Array.isArray(p.tip_suggestions) ? p.tip_suggestions.join(', ') : '',
    };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, b, pub] = await Promise.all([
        api.get('/admin/policies'),
        api.get('/admin/banned-customers'),
        api.get('/policies/public').catch(() => ({ data: {} })),
      ]);
      const decorated = decorate(p.data || {});
      decorated.kiosk_manager_exit_pin_set = !!pub.data?.kiosk_manager_exit_pin_set;
      setPolicy(decorated);
      setDraft(decorated);
      setBans(b.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load policies');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Local UI state for the Manager Exit PIN sub-panel.
  const [exitPinDraft, setExitPinDraft] = useState('');
  const [exitPinSaving, setExitPinSaving] = useState(false);
  const exitPinReason = exitPinDraft ? checkPinStrength(exitPinDraft) : null;

  const saveExitPin = async () => {
    if (!exitPinDraft) return;
    const reason = checkPinStrength(exitPinDraft);
    if (reason) { toast.error(reason); return; }
    setExitPinSaving(true);
    try {
      await api.post('/staff/kiosk/manager-pin', { pin: exitPinDraft });
      toast.success('Kiosk Manager Exit PIN saved.');
      setExitPinDraft('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save PIN');
    } finally { setExitPinSaving(false); }
  };

  const dirty = useMemo(() => {
    if (!policy || !draft) return false;
    return [
      'pickup_auto_cancel_hours',
      'delivery_no_show_grace_hours',
      'customer_strike_threshold',
      'redelivery_fee',
      'auto_ban_enabled',
      'notify_customer_on_cancel',
      'kitchen_day_reset_hour',
      'currency_code',
      'tax_rate_pct',          // UI uses percentage; we convert on save
      'tax_enabled',
      'tax_show_on_receipt',
      'tip_show_on_receipt',
      'tip_suggestions_enabled',
      'tip_suggestions_csv',
      'restaurant_callback_phone',
      'driver_contract_markdown',
    ].some((k) => String(policy[k] ?? '') !== String(draft[k] ?? ''));
  }, [policy, draft]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Parse the tip suggestions CSV ("10, 15, 20") back into an int[].
      const tipCsv = String(draft.tip_suggestions_csv ?? '').trim();
      const tipSuggestions = tipCsv
        ? tipCsv.split(/[,\s]+/).map((s) => parseInt(s, 10)).filter((n) => !Number.isNaN(n))
        : (policy?.tip_suggestions || [10, 15, 20]);
      const payload = {
        pickup_auto_cancel_hours: Number(draft.pickup_auto_cancel_hours) || 0,
        delivery_no_show_grace_hours: Number(draft.delivery_no_show_grace_hours) || 0,
        customer_strike_threshold: parseInt(draft.customer_strike_threshold, 10) || 0,
        redelivery_fee: Number(draft.redelivery_fee) || 0,
        auto_ban_enabled: !!draft.auto_ban_enabled,
        notify_customer_on_cancel: !!draft.notify_customer_on_cancel,
        // Kitchen day reset (0–23, hour at which the kitchen day rolls over)
        kitchen_day_reset_hour: Math.max(0, Math.min(23, parseInt(draft.kitchen_day_reset_hour ?? 0, 10) || 0)),
        // Pricing & receipts
        currency_code: (draft.currency_code || 'USD').toUpperCase(),
        tax_rate: Math.max(0, Math.min(100, Number(draft.tax_rate_pct) || 0)) / 100,
        tax_enabled: !!draft.tax_enabled,
        tax_show_on_receipt: !!draft.tax_show_on_receipt,
        tip_show_on_receipt: !!draft.tip_show_on_receipt,
        tip_suggestions_enabled: !!draft.tip_suggestions_enabled,
        tip_suggestions: tipSuggestions,
        restaurant_callback_phone: (draft.restaurant_callback_phone || '').trim(),
        driver_contract_markdown: String(draft.driver_contract_markdown ?? ''),
      };
      const { data } = await api.patch('/admin/policies', payload);
      const next = decorate(data);
      setPolicy(next);
      setDraft(next);
      // Push to module store so fmtMoney() flips immediately app-wide.
      try {
        notifyPolicyUpdated({
          currency_code: data.currency_code,
          tax_rate: data.tax_rate,
          tax_enabled: data.tax_enabled,
          tax_show_on_receipt: data.tax_show_on_receipt,
          tip_show_on_receipt: data.tip_show_on_receipt,
          tip_suggestions_enabled: data.tip_suggestions_enabled,
          tip_suggestions: data.tip_suggestions,
        });
      } catch (e) { /* noop */ }
      toast.success('Settings saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const reset = () => setDraft(policy);

  const doUnban = async () => {
    if (!confirmUnban) return;
    const phone = confirmUnban.phone;
    setConfirmUnban(null);
    try {
      const phoneEnc = encodeURIComponent(phone);
      await api.delete(`/admin/customers/${phoneEnc}/ban`);
      toast.success(`Unbanned ${phone}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unban');
    }
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      {/* === Pricing & Receipts === */}
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" /> Pricing &amp; receipts</CardTitle>
          <CardDescription className="text-xs">
            Controls the currency every channel displays, how tax is calculated on new orders, and what appears on printed receipts. Saving here updates the customer storefront, POS, KDS and driver app instantly.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {loading && <p className="text-sm text-muted-foreground">Loading settings…</p>}
          {!loading && draft && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Currency</Label>
                  <Select
                    value={draft.currency_code || 'USD'}
                    onValueChange={(v) => set('currency_code', v)}
                  >
                    <SelectTrigger data-testid="policy-currency-select" className="mt-1">
                      <SelectValue placeholder="Currency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">$ USD — US Dollar</SelectItem>
                      <SelectItem value="EUR">€ EUR — Euro</SelectItem>
                      <SelectItem value="GBP">£ GBP — British Pound</SelectItem>
                      <SelectItem value="XOF">CFA XOF — West African Franc</SelectItem>
                      <SelectItem value="XAF">CFA XAF — Central African Franc</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                    Symbol and formatting are applied across every screen, including menus and the printed receipt.
                  </p>
                </div>
                <Field
                  data-testid="policy-tax-rate"
                  label="Tax rate"
                  suffix="%"
                  hint="Applied to the order subtotal when “Charge tax” is on. Set to 0 if you bake tax into menu prices."
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={draft.tax_rate_pct ?? 0}
                  onChange={(v) => set('tax_rate_pct', v)}
                />
              </div>

              <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3">
                <SwitchRow
                  data-testid="policy-tax-enabled"
                  label="Charge tax on new orders"
                  hint="When OFF, new orders are placed without any tax line (subtotal equals total)."
                  checked={!!draft.tax_enabled}
                  onCheckedChange={(v) => set('tax_enabled', v)}
                />
                <SwitchRow
                  data-testid="policy-tax-show-receipt"
                  label="Show tax on printed receipts"
                  hint="If OFF, the receipt hides the tax line even when tax was charged. The amount stays included in the total."
                  checked={!!draft.tax_show_on_receipt}
                  onCheckedChange={(v) => set('tax_show_on_receipt', v)}
                />
                <SwitchRow
                  data-testid="policy-tip-show-receipt"
                  label="Show tip line on printed receipts"
                  hint="Hides the tip row when no tip was entered, or removes it entirely if your venue does not accept tips."
                  checked={!!draft.tip_show_on_receipt}
                  onCheckedChange={(v) => set('tip_show_on_receipt', v)}
                />
                <SwitchRow
                  data-testid="policy-tip-suggestions"
                  label="Suggest tip amounts on receipts &amp; checkout"
                  hint="Adds a row of percentage shortcuts (e.g. 10 / 15 / 20%) at the bottom of receipts and in the customer checkout."
                  checked={!!draft.tip_suggestions_enabled}
                  onCheckedChange={(v) => set('tip_suggestions_enabled', v)}
                />
                {draft.tip_suggestions_enabled && (
                  <Field
                    data-testid="policy-tip-csv"
                    label="Suggested tip percentages"
                    hint="Comma-separated list of percentages (up to 5)."
                    suffix="%"
                    value={draft.tip_suggestions_csv ?? ''}
                    onChange={(v) => set('tip_suggestions_csv', v)}
                  />
                )}
              </div>

              <PricingPreview draft={draft} />

              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
                <Button data-testid="settings-save" onClick={save} disabled={!dirty || saving}>
                  <Save className="mr-1 h-4 w-4" /> {saving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button data-testid="settings-reset" variant="ghost" onClick={reset} disabled={!dirty || saving}>
                  Reset
                </Button>
                {policy?.updated_at && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    Last updated {fmtTimeAgo(policy.updated_at)}
                  </span>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* === Kiosk Manager Exit PIN === */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-primary" /> Kiosk Manager Exit PIN</CardTitle>
          <CardDescription className="text-xs">
            Used to leave kiosk mode on any locked device (entrance kiosk, iPad table mode, KDS). Five scattered digits — never sequential or repeating.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-border bg-secondary/40 p-2.5 text-xs">
            {policy?.kiosk_manager_exit_pin_set ? (
              <p className="flex items-center gap-1.5 text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                A manager exit PIN is configured. Enter a new one to rotate it &mdash; the old one is replaced immediately.
              </p>
            ) : (
              <p className="flex items-center gap-1.5 text-amber-800">
                <AlertTriangle className="h-3.5 w-3.5" />
                No PIN set yet. Kiosks will fall back to a legacy default until you configure one here.
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">New exit PIN</Label>
            <Input
              data-testid="policy-exit-pin-input"
              type="password"
              inputMode="numeric"
              maxLength={5}
              autoComplete="new-password"
              placeholder="•••••"
              value={exitPinDraft}
              onChange={(e) => setExitPinDraft(e.target.value.replace(/\D/g, '').slice(0, 5))}
              className="mt-1 h-10 text-center font-mono text-lg tracking-[0.5em]"
            />
            {exitPinDraft && exitPinReason && (
              <p data-testid="policy-exit-pin-error" className="mt-1 text-xs text-rose-600">{exitPinReason}</p>
            )}
            {exitPinDraft && !exitPinReason && (
              <p className="mt-1 text-xs text-emerald-700"><CheckCircle2 className="mr-1 inline h-3 w-3" /> Strong PIN.</p>
            )}
            <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
              Examples of accepted PINs: <span className="font-mono">73914</span>, <span className="font-mono">28471</span>, <span className="font-mono">90835</span>. Rejected: <span className="font-mono">12345</span>, <span className="font-mono">77777</span>, <span className="font-mono">54321</span>.
            </p>
          </div>
          <Button
            data-testid="policy-exit-pin-save"
            onClick={saveExitPin}
            disabled={!exitPinDraft || !!exitPinReason || exitPinSaving}
          >
            <Save className="mr-1 h-4 w-4" /> {exitPinSaving ? 'Saving…' : (policy?.kiosk_manager_exit_pin_set ? 'Rotate PIN' : 'Save PIN')}
          </Button>
        </CardContent>
      </Card>

      {/* === Banned customers list === */}
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldX className="h-5 w-5 text-rose-600" /> Banned customers</CardTitle>
          <CardDescription className="text-xs">
            Banned phones cannot place new orders on any channel. Lift a ban only after speaking with the customer.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-muted-foreground">Loading bans…</p>}
          {!loading && bans.length === 0 && (
            <div className="flex flex-col items-center gap-1 rounded-md border border-dashed border-border py-6 text-center text-xs text-muted-foreground">
              <ShieldCheck className="h-5 w-5 opacity-60" />
              No bans on record. Customers are auto-banned at the configured strike threshold.
            </div>
          )}
          {!loading && bans.length > 0 && (
            <ul data-testid="admin-bans-list" className="divide-y divide-border rounded-md border border-border">
              {bans.map((b) => (
                <li key={b.phone} data-testid="admin-ban-row" className="flex items-start justify-between gap-2 p-3 text-sm">
                  <div className="min-w-0">
                    <p className="inline-flex items-center gap-1.5 font-mono">
                      <Phone className="h-3 w-3" /> {b.phone}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {b.auto ? 'Auto-ban' : 'Manual ban'} &middot; {fmtTimeAgo(b.banned_at)}
                    </p>
                    {b.reason && (
                      <p className="mt-0.5 text-xs italic text-muted-foreground">&ldquo;{b.reason}&rdquo;</p>
                    )}
                  </div>
                  <Button
                    data-testid="admin-ban-unban"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={() => setConfirmUnban(b)}
                  >
                    <ShieldCheck className="mr-1 h-3 w-3" /> Unban
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* === Driver dispatch SMS + contract === */}
      <Card className="lg:col-span-5" data-testid="policy-driver-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-primary" /> Driver dispatch &amp; contract</CardTitle>
          <CardDescription className="text-xs">
            Set the callback number drivers can call (SMS fallback when offline) and the agreement they must accept before being added.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!loading && draft && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Restaurant callback phone</Label>
                  <Input
                    data-testid="policy-callback-phone-input"
                    value={draft.restaurant_callback_phone || ''}
                    onChange={(e) => set('restaurant_callback_phone', e.target.value)}
                    placeholder="+15551234567"
                  />
                  <p className="text-[11px] text-muted-foreground">Included in the SMS sent to drivers so they can call in to claim if their internet is down.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Current contract version</Label>
                  <div className="flex h-9 items-center rounded-md border border-input bg-secondary/30 px-3 text-sm tabular">
                    v{Number(policy?.driver_contract_version) || 1}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Auto-increments whenever the text below changes. Each driver records the version they accepted.</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Driver contract (Markdown)</Label>
                <Textarea
                  data-testid="policy-contract-input"
                  rows={12}
                  value={draft.driver_contract_markdown ?? ''}
                  onChange={(e) => set('driver_contract_markdown', e.target.value)}
                  className="font-mono text-xs"
                />
                <p className="text-[11px] text-muted-foreground">Plain Markdown. Use # / ## for headings and - for bullet lists. Drivers can print this before accepting.</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* === No-show & ban policies === */}
      <Card className="lg:col-span-5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-primary" /> No-show &amp; ban policies</CardTitle>
          <CardDescription className="text-xs">
            Controls auto-cancel sweeps for stale pickups, strike accumulation, and automatic banning of repeat offenders. Changes apply immediately to every channel (POS, web, iPad).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {!loading && draft && (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field
                  data-testid="policy-pickup-auto-cancel-hours"
                  label="Auto-cancel pickup after"
                  suffix="hours"
                  hint="Ready-for-pickup orders that sit untouched this long are auto-cancelled and counted as a no-show strike."
                  type="number"
                  step="0.5"
                  min="0"
                  value={draft.pickup_auto_cancel_hours ?? 0}
                  onChange={(v) => set('pickup_auto_cancel_hours', v)}
                />
                <Field
                  data-testid="policy-delivery-grace-hours"
                  label="Delivery no-show grace"
                  suffix="hours"
                  hint="How long the driver should attempt delivery before reporting a no-show."
                  type="number"
                  step="0.5"
                  min="0"
                  value={draft.delivery_no_show_grace_hours ?? 0}
                  onChange={(v) => set('delivery_no_show_grace_hours', v)}
                />
                <Field
                  data-testid="policy-strike-threshold"
                  label="Auto-ban after"
                  suffix="strikes"
                  hint="Cancelled / no-show orders count as strikes. Hitting this number bans the customer phone."
                  type="number"
                  step="1"
                  min="0"
                  value={draft.customer_strike_threshold ?? 0}
                  onChange={(v) => set('customer_strike_threshold', v)}
                />
                <Field
                  data-testid="policy-redelivery-fee"
                  label="Re-delivery fee"
                  prefix={currencySymbol(draft.currency_code)}
                  hint="Optional surcharge applied if the customer asks to retry a failed delivery."
                  type="number"
                  step="0.5"
                  min="0"
                  value={draft.redelivery_fee ?? 0}
                  onChange={(v) => set('redelivery_fee', v)}
                />
              </div>

              <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3">
                <SwitchRow
                  data-testid="policy-auto-ban-enabled"
                  label="Auto-ban enabled"
                  hint="Globally enable or disable the automatic ban when the strike threshold is reached. Manual bans always work."
                  checked={!!draft.auto_ban_enabled}
                  onCheckedChange={(v) => set('auto_ban_enabled', v)}
                />
                <SwitchRow
                  data-testid="policy-notify-on-cancel"
                  label="Notify customer by SMS on cancel"
                  hint="Sends a courtesy SMS whenever an order is cancelled (manual, no-show, or auto-timeout)."
                  checked={!!draft.notify_customer_on_cancel}
                  onCheckedChange={(v) => set('notify_customer_on_cancel', v)}
                />
              </div>

              {/* Kitchen day rollover — orders unfinished at this hour are
                  flipped to "overdue" so the kitchen lanes wipe clean each
                  morning. Common values: 0 (midnight, default), 2-4
                  (post-late-night service). */}
              <div className="grid gap-4 border-t border-border pt-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" /> Kitchen day reset hour (0&ndash;23)
                  </Label>
                  <Input
                    data-testid="policy-kitchen-reset-hour"
                    type="number"
                    min="0"
                    max="23"
                    step="1"
                    value={draft.kitchen_day_reset_hour ?? 0}
                    onChange={(e) => set('kitchen_day_reset_hour', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    At this hour each day the KDS lanes are cleared. Orders
                    placed the previous day that were never finished are
                    auto-flagged <span className="font-semibold text-amber-700">overdue</span> and
                    surfaced under the POS &raquo; Overdue filter for the cashier to audit.
                    Default <strong>0</strong> (midnight).
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
                <Button data-testid="policy-save" onClick={save} disabled={!dirty || saving}>
                  <Save className="mr-1 h-4 w-4" /> {saving ? 'Saving\u2026' : 'Save changes'}
                </Button>
                <Button data-testid="policy-reset" variant="ghost" onClick={reset} disabled={!dirty || saving}>
                  Reset
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmUnban} onOpenChange={(o) => { if (!o) setConfirmUnban(null); }}>
        <AlertDialogContent data-testid="admin-unban-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Lift ban for {confirmUnban?.phone}?</AlertDialogTitle>
            <AlertDialogDescription>
              The customer will be able to place orders again immediately. Their strike history is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Back</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-unban-confirm-accept" onClick={doUnban}>
              Yes, unban
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Tiny live preview chip — shows how a sample order's totals will look
// in the chosen currency with the chosen tax settings.
function PricingPreview({ draft }) {
  const subtotal = 12.5;
  const rate = (Number(draft.tax_rate_pct) || 0) / 100;
  const tax = draft.tax_enabled ? Math.round(subtotal * rate * 100) / 100 : 0;
  const total = Math.round((subtotal + tax) * 100) / 100;
  const fmt = (n) => formatMoney(n, draft.currency_code);
  return (
    <div data-testid="policy-pricing-preview" className="rounded-lg border border-dashed border-border bg-background/60 p-3 text-xs">
      <p className="font-semibold uppercase tracking-wider text-muted-foreground">Live preview</p>
      <div className="mt-2 flex items-center justify-between text-sm">
        <span>Subtotal</span><span className="tabular">{fmt(subtotal)}</span>
      </div>
      {draft.tax_enabled && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Tax ({Number(draft.tax_rate_pct || 0).toFixed(2)}%)</span>
          <span className="tabular">{fmt(tax)}</span>
        </div>
      )}
      <div className="mt-1 flex items-center justify-between border-t border-dashed border-border pt-1 text-sm font-semibold">
        <span>Total</span><span className="tabular">{fmt(total)}</span>
      </div>
    </div>
  );
}

function currencySymbol(code) {
  switch ((code || 'USD').toUpperCase()) {
    case 'EUR': return '€';
    case 'GBP': return '£';
    case 'XOF':
    case 'XAF': return 'CFA';
    default: return '$';
  }
}

function Field({ label, hint, value, onChange, type = 'text', step, min, prefix, suffix, ...rest }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1 flex items-stretch overflow-hidden rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
        {prefix && (
          <span className="flex items-center bg-secondary/50 px-2 text-xs text-muted-foreground">{prefix}</span>
        )}
        <input
          {...rest}
          type={type}
          step={step}
          min={min}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-full border-0 bg-transparent px-2 text-sm outline-none"
        />
        {suffix && (
          <span className="flex items-center bg-secondary/50 px-2 text-xs text-muted-foreground">{suffix}</span>
        )}
      </div>
      {hint && <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{hint}</p>}
    </div>
  );
}

function SwitchRow({ label, hint, checked, onCheckedChange, ...rest }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {hint && <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{hint}</p>}
      </div>
      <Switch {...rest} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}
